import OneSignal from 'react-onesignal';
import { isNative, nativeOneSignalLogin, nativeOneSignalLogout } from '../capacitor';

let isInitialized = false;
let initializationFailed = false;

const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

export const initOneSignal = async () => {
  const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
  
  if (!appId || appId === 'YOUR_ONESIGNAL_APP_ID_HERE') {
    console.warn('OneSignal App ID not configured - push notifications disabled');
    initializationFailed = true;
    return false;
  }

  if (isInitialized) {
    return true;
  }

  try {
    await OneSignal.init({
      appId: appId,
      safari_web_id: 'web.onesignal.auto.16fe94fe-85b7-4f18-b294-6465f1482156',
      allowLocalhostAsSecureOrigin: true,
      notifyButton: { enable: false },
      serviceWorkerParam: { scope: '/' },
      serviceWorkerPath: '/OneSignalSDKWorker.js',
    });
    
    isInitialized = true;
    console.log('OneSignal initialized successfully with App ID:', appId.slice(0, 8) + '...');
    
    // If browser already has permission, auto-opt into OneSignal push subscription
    if (isNotificationSupported() && Notification.permission === 'granted') {
      try {
        const optedIn = await OneSignal.User.PushSubscription.optedIn;
        if (!optedIn) {
          await OneSignal.User.PushSubscription.optIn();
          console.log('Auto-opted into OneSignal push (browser permission was already granted)');
        }
      } catch (e) {
        console.warn('Auto-opt-in attempt:', e);
      }
    }
    
    return true;
  } catch (error) {
    if (error.message?.includes('already initialized')) {
      isInitialized = true;
      return true;
    }
    console.error('OneSignal initialization error:', error);
    initializationFailed = true;
    return false;
  }
};

export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported on this device/browser');
    return false;
  }
  
  // Retry initialization once if it previously failed (e.g. transient error)
  if (!isInitialized) {
    initializationFailed = false;
    await initOneSignal();
  }
  
  try {
    if (isInitialized && !initializationFailed) {
      await OneSignal.Slidedown.promptPush({ force: true });
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const isSubscribed = await OneSignal.User.PushSubscription.optedIn;
      console.log('OneSignal subscription status after prompt:', isSubscribed);
      
      // If browser granted but OneSignal didn't register, force opt-in
      if (!isSubscribed && Notification.permission === 'granted') {
        await OneSignal.User.PushSubscription.optIn();
        const retryCheck = await OneSignal.User.PushSubscription.optedIn;
        console.log('Forced opt-in result:', retryCheck);
        return retryCheck;
      }
      return isSubscribed;
    }
    
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting permission:', error);
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (e) {
      return false;
    }
  }
};

// Check if notifications are fully enabled (browser + OneSignal)
export const isNotificationEnabled = () => {
  if (!isNotificationSupported()) return false;
  return Notification.permission === 'granted';
};

// Whether the OneSignal service itself is usable on this site
export const isPushServiceAvailable = () => isInitialized && !initializationFailed;

// Check OneSignal subscription status (async)
export const getOneSignalSubscriptionStatus = async () => {
  if (!isInitialized || initializationFailed) return false;
  try {
    return await OneSignal.User.PushSubscription.optedIn;
  } catch {
    return false;
  }
};

export const setExternalUserId = async (userId) => {
  if (isNative) {
    return nativeOneSignalLogin(userId);
  }
  if (!isInitialized || initializationFailed) return;
  
  try {
    await OneSignal.login(userId);
    console.log('OneSignal external user ID set:', userId);
    
    // After login, ensure push subscription is active if browser has permission
    if (isNotificationSupported() && Notification.permission === 'granted') {
      try {
        const optedIn = await OneSignal.User.PushSubscription.optedIn;
        if (!optedIn) {
          await OneSignal.User.PushSubscription.optIn();
          console.log('Auto-opted into push after login');
        }
      } catch (e) {
        console.warn('Post-login opt-in attempt:', e);
      }
    }
  } catch (error) {
    console.error('Error setting external user ID:', error);
  }
};

export const removeExternalUserId = async () => {
  if (isNative) {
    return nativeOneSignalLogout();
  }
  if (!isInitialized || initializationFailed) return;
  
  try {
    await OneSignal.logout();
    console.log('External user ID removed');
  } catch (error) {
    console.error('Error removing external user ID:', error);
  }
};

export const setUserTags = async (tags) => {
  if (!isInitialized || initializationFailed) return;
  
  try {
    for (const [key, value] of Object.entries(tags)) {
      await OneSignal.User.addTag(key, String(value));
    }
  } catch (error) {
    console.error('Error setting tags:', error);
  }
};

export default OneSignal;
